import React, { useState, useEffect } from 'react';
import { ChevronDown, Search, DollarSign, Code, Clock, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import codeslateIcon from '../../assets/codeslate-logo.png';
import './FAQPage.css';

const FAQ_DATA = [
  {
    category: 'Pricing & Billing',
    icon: DollarSign,
    questions: [
      {
        question: 'How does the $3 per pad billing work?',
        answer: 'You only pay $3 when you create a new coding pad. No monthly subscriptions, no hidden fees. If you don\'t use any pads in a month, you pay nothing.'
      },
      {
        question: 'What happens if I exceed my expected usage?',
        answer: 'There\'s no such thing as exceeding usage with CodeSlate. You pay per pad, so you\'re always in control of your costs. No surprise bills or overage charges.'
      },
      {
        question: 'Is there a free trial?',
        answer: 'Yes! You can try our sandbox for free to experience the platform. When you\'re ready to conduct real interviews, each pad costs just $3.'
      },
      {
        question: 'What if I only conduct a few interviews per month?',
        answer: 'That\'s exactly why CodeSlate is perfect for you! With competitors, you\'d pay $100+ monthly even for just 2-3 interviews. With CodeSlate, you\'d pay only $6-9 for the same usage.'
      },
      {
        question: 'Do you offer volume discounts?',
        answer: 'Our $3 per pad pricing is already highly competitive. However, for companies conducting 100+ interviews monthly, we offer custom enterprise pricing. Contact our sales team for details.'
      }
    ]
  },
  {
    category: 'Technical & Features',
    icon: Code,
    questions: [
      {
        question: 'What programming languages are supported?',
        answer: 'We support various programming languages including JavaScript, Python, Java, C++, Go, Rust, and more. Each language includes syntax highlighting and code execution capabilities.'
      },
      {
        question: 'Can I execute code during interviews?',
        answer: 'Absolutely! CodeSlate includes built-in code execution with instant output. You can run code directly in the editor without any setup or configuration.'
      },
      {
        question: 'How does real-time collaboration work?',
        answer: 'Multiple users can edit code simultaneously with live cursor tracking. Changes appear instantly for all participants, just like Google Docs but for code.'
      },
      {
        question: 'Can I use my own IDE or do I have to use your editor?',
        answer: 'CodeSlate provides a full-featured code editor with syntax highlighting, but you can also copy code to external IDEs if needed. The editor is designed to be familiar and intuitive.'
      },
      {
        question: 'What browsers are supported?',
        answer: 'CodeSlate works on all modern browsers including Chrome, Firefox, Safari, and Edge. We recommend using the latest version for the best experience.'
      }
    ]
  },
  {
    category: 'Usage & Setup',
    icon: Clock,
    questions: [
      {
        question: 'How quickly can I get started?',
        answer: 'You can start conducting interviews in under 2 minutes. Simply create an account, create a pad, and share the link with your candidate. No complex setup required.'
      },
      {
        question: 'Do I need to install any software?',
        answer: 'No installation required! CodeSlate runs entirely in your web browser. Just create an account and you\'re ready to go.'
      },
      {
        question: 'Can I invite multiple interviewers to the same pad?',
        answer: 'Yes, you can invite unlimited interviewers to collaborate on the same pad. Perfect for panel interviews or technical discussions with your team.'
      },
      {
        question: 'Do candidates need to create an account?',
        answer: 'No! Candidates can join your pad instantly using just the link you share. No account creation required for candidates, making the process frictionless.'
      }
    ]
  },
  {
    category: 'Support & Reliability',
    icon: HelpCircle,
    questions: [
      {
        question: 'What if I need help during an interview?',
        answer: 'Our platform is designed to be intuitive and reliable. If you encounter any issues, our support team is available to help. We maintain 99.9% uptime to ensure your interviews run smoothly.'
      },
      {
        question: 'Can I get a refund if I\'m not satisfied?',
        answer: 'We\'re confident you\'ll love CodeSlate, but if you\'re not satisfied with a pad, we\'ll provide a full refund. Contact our support team and we\'ll make it right.'
      },
      {
        question: 'Is CodeSlate suitable for large companies?',
        answer: 'Yes! CodeSlate scales from individual recruiters to large enterprises. With unlimited users and pay-per-use pricing, it\'s cost-effective at any scale.'
      }
    ]
  }
];

const FAQPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('Pricing & Billing');
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleQuestion = (questionId: string) => {
    const newOpenQuestions = new Set(openQuestions);
    if (newOpenQuestions.has(questionId)) {
      newOpenQuestions.delete(questionId);
    } else {
      newOpenQuestions.add(questionId);
    }
    setOpenQuestions(newOpenQuestions);
  };

  const filteredFAQ = FAQ_DATA.map(category => ({
    ...category,
    questions: category.questions.filter(q => 
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const currentCategory = filteredFAQ.find(cat => cat.category === activeCategory) || filteredFAQ[0];

  return (
    <div className="faq-page">
      {/* Header */}
      <header className="faq-header">
        <div className="faq-nav">
          <Link to="/" className="faq-logo">
            <img src={codeslateIcon} alt="CodeSlate" className="logo-icon" />
            <span>CodeSlate</span>
          </Link>
          <Link to="/" className="back-to-home">
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="faq-main">
        <div className="faq-container">
          <h1 className="faq-title">Frequently Asked Questions</h1>
          <p className="faq-subtitle">Everything you need to know about CodeSlate</p>

          {/* Search Bar */}
          <div className="faq-search">
            <div className="search-input-container">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div className="faq-categories">
            {filteredFAQ.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.category}
                  className={`category-tab ${activeCategory === category.category ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category.category)}
                >
                  <IconComponent size={20} />
                  <span>{category.category}</span>
                </button>
              );
            })}
          </div>

          {/* Questions */}
          <div className="faq-questions">
            {currentCategory?.questions.map((faq, index) => {
              const questionId = `${currentCategory.category}-${index}`;
              const isOpen = openQuestions.has(questionId);
              
              return (
                <div key={questionId} className="faq-item">
                  <button
                    className="faq-question"
                    onClick={() => toggleQuestion(questionId)}
                  >
                    <span>{faq.question}</span>
                    <div className={`chevron ${isOpen ? 'open' : ''}`}>
                      <ChevronDown size={20} />
                    </div>
                  </button>
                  
                  {isOpen && (
                    <div className="faq-answer">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Contact CTA */}
          <div className="faq-cta">
            <p>Still have questions?</p>
            <Link to="/contact" className="faq-contact-btn">
              Contact Support
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FAQPage;
